import { BoardConfig } from './models';
import { Vector3, Quaternion } from 'three';

const enableDebugMode = true;

export const boardConfig: BoardConfig = {
    listensOnScroll: false,
    frames: 25,
    debug: enableDebugMode && location.search === '?debug',
    assets: [],
    animations: {},
    depthAdjustmentList: [],
    maxOpacity: 1,
    linesToDraw: [
        { source: 'scanner', target: 'server_1' },
        { source: 'scanner', target: 'server_2' },
        { source: 'scanner', target: 'server_3' }
    ],
    opacityAdjustmentList: [
        'Sphere',
        'human',
        'server_1',
        'laptop',
        'server_2',
        'server_3',
        'wifi',
        'head',
        'info',
        'info_national_police',
        'hacker_info',
        'info_description_last_usage',
        'info_description_terrorist',
        'info_description_police'
    ],
    // Lower number is faster: Will be the fps * cameraSpeed.
    cameraSpeed: 3,
    assetTransformations: {
        scanner: {
            finger: {
                name: 'scanner',
                position: new Vector3(-1.03, 15.07, 6.59),
                quaternion: new Quaternion(0.15, -0.69, 0.69, 0.15)
            },
            eyes: {
                name: 'scanner',
                position: new Vector3(-0.07, 21.05, 4.82),
                quaternion: new Quaternion(0.74, 0.0, 0.0, 0.67)
            },
            initial: {
                name: 'scanner',
                position: new Vector3(-0.07, 21.34, 16.34),
                quaternion: new Quaternion(0.71, 0.0, 0.0, 0.71)
            },
            picture: {
                name: 'scanner',
                position: new Vector3(-0.07, 21.34, 8.63),
                quaternion: new Quaternion(0.97, 0.0, 0.0, 0.23)
            }
        }
    },
    cameraConfigs: {
        scanner: {
            position: new Vector3(-1.47, 23.85, 19.16),
            quaternion: new Quaternion(-0.39, -0.23, -0.1, 0.89),
            target: new Vector3(7.9, 2.56, 2.09)
        },
        eyes: {
            position: new Vector3(-5.65, 25.35, 8.07),
            quaternion: new Quaternion(-0.38, -0.31, -0.14, 0.86),
            target: new Vector3(4.63, 8.05, -4.11)
        },
        info: {
            position: new Vector3(-5.29, 25.41, 21.3),
            quaternion: new Quaternion(-0.25, -0.24, -0.06, 0.94),
            target: new Vector3(7.86, 9.5, -3.22)
        },
        display_finger: {
            position: new Vector3(-2.04, 17.62, 5.22),
            quaternion: new Quaternion(0.18, 0.82, 0.37, -0.39),
            target: new Vector3(5.11, 7.12, 11.07)
        },
        display_eyes: {
            position: new Vector3(-1.19, 22.79, 5.07),
            quaternion: new Quaternion(-0.39, -0.29, -0.13, 0.87),
            target: new Vector3(5.75, 9.81, -4.34)
        },
        display_initial: {
            position: new Vector3(-0.6, 22.79, 16.44),
            quaternion: new Quaternion(-0.4, -0.18, -0.08, 0.9),
            target: new Vector3(6.17, 3.47, 0.16)
        },
        display_initial_close: {
            position: new Vector3(0.0, 22.33, 16.05),
            quaternion: new Quaternion(-0.44, -0.02, -0.01, 0.9),
            target: new Vector3(0.55, 2.35, 0.57)
        },
        finger: {
            position: new Vector3(-6.34, 19.42, -0.51),
            quaternion: new Quaternion(0.18, 0.8, 0.31, -0.48),
            target: new Vector3(6.87, 5.96, 6.58)
        },
        picture: {
            position: new Vector3(-4.87, 22.1, -2.97),
            quaternion: new Quaternion(0.06, 0.94, 0.22, -0.26),
            target: new Vector3(6.7, 10.77, 16.74)
        },
        server: {
            position: new Vector3(149.23, 16.34, 90.2),
            quaternion: new Quaternion(-0.26, -0.51, -0.17, 0.8),
            target: new Vector3(163.72, 4.49, 83.54)
        },
        server_data: {
            position: new Vector3(177.13, 66.18, 270.37),
            quaternion: new Quaternion(-0.26, 0.2, 0.05, 0.94),
            target: new Vector3(133.55, 0.2, 169.79)
        },
        server_data_top: {
            position: new Vector3(93.55, 92.36, 96.55),
            quaternion: new Quaternion(0.15, 0.85, 0.36, -0.35),
            target: new Vector3(117.39, 57.1, 120.58)
        },
        server_data_close: {
            position: new Vector3(180.5, 18.65, 113.73),
            quaternion: new Quaternion(-0.18, 0.29, 0.05, 0.94),
            target: new Vector3(159.03, 4.03, 82.64)
        },
        data: {
            position: new Vector3(-5.46, 26.59, 11.07),
            quaternion: new Quaternion(0.11, 0.88, 0.25, -0.39),
            target: new Vector3(2.03, 20.34, 17.71)
        },
        keyboard_eyes: {
            position: new Vector3(0.47, 22.82, 6.95),
            quaternion: new Quaternion(-0.42, 0.04, 0.02, 0.91),
            target: new Vector3(-0.73, 8.51, -5.16)
        },
        keyboard_initial: {
            position: new Vector3(-0.66, 22.33, 17.5),
            quaternion: new Quaternion(-0.34, -0.19, -0.07, 0.92),
            target: new Vector3(7.21, 5.13, -0.78)
        },
        keyboard_initial_close: {
            position: new Vector3(-0.53, 22.24, 17.02),
            quaternion: new Quaternion(-0.34, -0.19, -0.07, 0.92),
            target: new Vector3(7.19, 5.29, -0.94)
        },
        hacker_info: {
            position: new Vector3(-2.55, 25.84, 24.48),
            quaternion: new Quaternion(-0.27, -0.31, -0.09, 0.91),
            target: new Vector3(14.82, 7.62, 2.53)
        },
        info_close: {
            position: new Vector3(-2.77, 23.22, 16.32),
            quaternion: new Quaternion(-0.19, -0.24, -0.05, 0.95),
            target: new Vector3(8.31, 13.45, -4.18)
        },
        hacker_info_close: {
            position: new Vector3(-2.36, 24.32, 20.57),
            quaternion: new Quaternion(-0.2, -0.36, -0.08, 0.91),
            target: new Vector3(15.62, 12.05, 1.23)
        }
    }
};
